import React, { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, BookOpen, Phone } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEO, { breadcrumbSchema } from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { findInsightTopic, insightTopics } from '@/data/insightTopics';
import { articles as originalArticles } from '@/data/articlesData';
import { sellerArticles } from '@/data/sellerArticlesData';
import { residentialSellerArticles } from '@/data/residentialSellerArticlesData';
import { landownerArticles } from '@/data/landownerArticlesData';
import { developmentInsightsArticles } from '@/data/developmentInsightsArticlesData';
import { sellerSegmentArticles } from '@/data/sellerSegmentArticlesData';
import { allSuburbArticles } from '@/data/suburbs';
import { sellToDeveloperArticles } from '@/data/sellToDeveloperArticles';
import { valuationLeadGenArticles } from '@/data/valuationLeadGenArticles';
import { allPlanningArticles } from '@/data/councilPlanningArticles';
import { olympicsImpactArticles } from '@/data/olympicsImpactArticles';
import { stationArticles } from '@/data/stationArticles';
import { quarterlyMarketReports, zoningAlertArticles } from '@/data/councilMarketArticles';
import { topListArticles } from '@/data/topListArticles';
import { comparisonArticles } from '@/data/comparisonArticles';
import { zoningExplainerArticles, canIBuildArticles, planningMistakeArticles, overlayExplainerArticles } from '@/data/townPlanningArticles';
import { aeoPillarArticles } from '@/data/aeoPillarArticles';
import { aeoThoughtLeadershipArticles } from '@/data/aeoThoughtLeadershipArticles';
import { aeoQuickAnswerArticles } from '@/data/aeoQuickAnswerArticles';
import { aeoDeveloperStrategyArticles } from '@/data/aeoDeveloperStrategyArticles';
import { aeoDeveloperStrategyBatch2 } from '@/data/aeoDeveloperStrategyBatch2';
import { aeoSellerEducationBatch } from '@/data/aeoSellerEducationBatch';
import { aeoCommercialInstitutionalBatch } from '@/data/aeoCommercialInstitutionalBatch';
import { aeoCommercialInstitutionalBatch2 } from '@/data/aeoCommercialInstitutionalBatch2';
import { aeoTransactionStructuresBatch } from '@/data/aeoTransactionStructuresBatch';
import { aeoAiFutureBatch } from '@/data/aeoAiFutureBatch';
import { aeoPillarSellerGapsBatch } from '@/data/aeoPillarSellerGapsBatch';
import { aeoSuburbDeepDivesBatch } from '@/data/aeoSuburbDeepDivesBatch';
import { aeoQuarterlyReportsBatch } from '@/data/aeoQuarterlyReportsBatch';
import { aeoSellerEducationClosoutBatch } from '@/data/aeoSellerEducationClosoutBatch';

const ALL_ARTICLES = [
  ...originalArticles, ...sellerArticles, ...residentialSellerArticles, ...landownerArticles,
  ...developmentInsightsArticles, ...sellerSegmentArticles, ...allSuburbArticles,
  ...sellToDeveloperArticles, ...valuationLeadGenArticles, ...allPlanningArticles,
  ...olympicsImpactArticles, ...stationArticles, ...quarterlyMarketReports,
  ...zoningAlertArticles, ...topListArticles, ...comparisonArticles,
  ...zoningExplainerArticles, ...canIBuildArticles, ...planningMistakeArticles,
  ...overlayExplainerArticles, ...aeoPillarArticles, ...aeoThoughtLeadershipArticles,
  ...aeoQuickAnswerArticles, ...aeoDeveloperStrategyArticles, ...aeoDeveloperStrategyBatch2, ...aeoSellerEducationBatch, ...aeoCommercialInstitutionalBatch, ...aeoCommercialInstitutionalBatch2, ...aeoTransactionStructuresBatch, ...aeoAiFutureBatch, ...aeoPillarSellerGapsBatch, ...aeoSuburbDeepDivesBatch, ...aeoQuarterlyReportsBatch, ...aeoSellerEducationClosoutBatch,
];

const SITE = 'https://acres.au';

const InsightTopicPage = () => {
  const { slug } = useParams();
  const topic = findInsightTopic(slug);

  // Resolve articles for this topic (memoised)
  const matched = useMemo(() => {
    if (!topic) return [];
    const seen = new Set();
    return ALL_ARTICLES
      .filter((a) => {
        if (!a || !a.id || seen.has(a.id)) return false;
        if (!topic.matches(a)) return false;
        seen.add(a.id);
        return true;
      })
      .sort((a, b) => new Date(b.date || '2025-01-01') - new Date(a.date || '2025-01-01'));
  }, [topic]);

  if (!topic) return <Navigate to="/insights" replace />;

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: topic.h1,
    headline: topic.h1,
    description: topic.description,
    url: `${SITE}/insights/topic/${topic.slug}`,
    inLanguage: 'en-AU',
    isPartOf: { '@type': 'WebSite', name: 'ACRES', url: SITE },
    publisher: {
      '@type': 'Organization',
      name: 'ACRES — Australian Commercial & Residential Group',
      url: SITE,
    },
    about: { '@type': 'Thing', name: topic.title },
    keywords: topic.keywords?.join(', '),
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: matched.length,
      itemListElement: matched.slice(0, 50).map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${SITE}/insights/${a.id}`,
        name: a.title,
      })),
    },
  };

  return (
    <div className="min-h-screen bg-[#FFF4EA]">
      <SEO
        title={`${topic.h1} | ACRES Insights`}
        description={topic.description}
        canonical={`${SITE}/insights/topic/${topic.slug}`}
        ogImage="https://customer-assets.emergentagent.com/job_brand-foundation-4/artifacts/2q3b6r98_PGFX_DNC_20230719_0442.jpeg"
        schemas={[
          collectionSchema,
          breadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Insights', url: '/insights' },
            { name: topic.title },
          ]),
        ]}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-28 pb-12 sm:pt-32 sm:pb-16 px-6 sm:px-8 lg:px-16 bg-[#042E0C] text-[#FFF4EA] overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none" aria-hidden>
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#C4A36B]/30 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#C4A36B]/20 blur-3xl" />
        </div>
        <div className="max-w-5xl mx-auto relative">
          <Breadcrumbs
            items={[
              { name: 'Insights', href: '/insights' },
              { name: topic.title },
            ]}
            light
          />
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#C4A36B] mt-6 mb-4" data-testid="topic-eyebrow">
            ACRES Knowledge Hub · {matched.length} articles
          </p>
          <h1
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light leading-tight mb-6"
            data-testid="topic-h1"
          >
            {topic.h1}
          </h1>
          <p className="font-body text-base sm:text-lg leading-relaxed text-[#FFF4EA]/80 max-w-3xl">
            {topic.intro}
          </p>
        </div>
      </section>

      {/* Article grid */}
      <section className="px-6 sm:px-8 lg:px-16 py-16 sm:py-20">
        <div className="max-w-6xl mx-auto">
          {matched.length === 0 ? (
            <p className="text-center text-[#5B5B5B]" data-testid="topic-empty">
              No articles in this topic yet — check back soon, or explore <Link to="/insights" className="text-[#C4A36B] underline">all ACRES insights</Link>.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="topic-article-grid">
              {matched.map((a) => (
                <Link
                  key={a.id}
                  to={`/insights/${a.id}`}
                  className="group bg-white border border-[#042E0C]/10 hover:border-[#C4A36B] hover:shadow-lg transition-all p-6 flex flex-col"
                  data-testid={`topic-article-card-${a.id}`}
                >
                  <div className="flex items-center gap-2 mb-3 text-xs text-[#C4A36B] font-semibold tracking-[0.15em] uppercase">
                    <BookOpen size={12} /> {a.category || 'Insight'}
                  </div>
                  <h2 className="font-heading text-lg sm:text-xl font-light text-[#042E0C] mb-3 leading-snug group-hover:text-[#C4A36B] transition-colors">
                    {a.title}
                  </h2>
                  <p className="text-sm text-[#5B5B5B] leading-relaxed flex-1 line-clamp-3">
                    {a.excerpt || a.metaDescription}
                  </p>
                  <div className="mt-5 pt-4 border-t border-[#042E0C]/8 flex items-center justify-between text-xs text-[#5B5B5B]/70">
                    <span className="flex items-center gap-3">
                      {a.date && (
                        <span className="flex items-center gap-1.5">
                          <Calendar size={11} />
                          {new Date(a.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                      {a.readTime && (
                        <span className="flex items-center gap-1.5"><Clock size={11} /> {a.readTime}</span>
                      )}
                    </span>
                    <span className="text-[#C4A36B] group-hover:translate-x-1 transition-transform">
                      <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Author + Entity reinforcement (same canonical block as articles) */}
      <section className="px-6 sm:px-8 lg:px-16 pb-16 sm:pb-24">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Author card */}
          <Link
            to="/team/daniel-mccormack"
            className="bg-[#042E0C] p-6 sm:p-8 flex flex-col items-start gap-4 hover:bg-[#0D7B3E] transition-colors lg:col-span-1"
            data-testid="topic-author-card"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_brand-foundation-4/artifacts/ibqouwi8_F9992471-15B8-4303-8FF6-01B35D3598D1.png"
              alt="Daniel McCormack — Managing Director, ACRES"
              className="w-16 h-16 rounded-full object-cover"
            />
            <div>
              <p className="font-heading text-lg text-[#FFF4EA] mb-1">Daniel McCormack</p>
              <p className="text-xs text-[#FFF4EA]/60 mb-3">Managing Director · ACRES</p>
              <p className="text-sm text-[#FFF4EA]/80 leading-relaxed">
                Daniel leads ACRES' development site and strategic acquisitions practice across South-East Queensland and is the lead author of this knowledge hub.
              </p>
              <div className="mt-4 flex items-center gap-2 text-xs text-[#C4A36B] font-medium">
                <Phone size={11} /> 07 3096 0542
              </div>
            </div>
          </Link>

          {/* Entity reinforcement — same canonical block as articles */}
          <aside
            className="lg:col-span-2 border border-[#042E0C]/10 bg-white p-6 sm:p-8"
            data-testid="topic-entity-block"
            aria-label="About ACRES"
          >
            <h2 className="font-heading text-xl font-light text-[#042E0C] mb-3">About ACRES</h2>
            <p className="text-sm text-[#042E0C]/80 leading-relaxed mb-3">
              <strong>ACRES — Australian Commercial &amp; Residential Group</strong> is a Brisbane-based property group specialising in
              {' '}<Link to="/development-sites" className="text-[#042E0C] underline decoration-[#C4A36B] underline-offset-2 hover:text-[#C4A36B]">development site sales</Link>,
              {' '}<Link to="/project-marketing" className="text-[#042E0C] underline decoration-[#C4A36B] underline-offset-2 hover:text-[#C4A36B]">project marketing</Link>,
              {' '}<Link to="/strategic-acquisitions" className="text-[#042E0C] underline decoration-[#C4A36B] underline-offset-2 hover:text-[#C4A36B]">strategic property acquisitions</Link>,
              residential real estate, capital structuring, and in-house property development.
            </p>
            <p className="text-sm text-[#042E0C]/80 leading-relaxed mb-3">
              Operating across Brisbane and South East Queensland, ACRES works with sellers, developers, investors, and project partners to identify value, shape strategy, and deliver stronger outcomes across residential, commercial, and development property markets.
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-xs">
              <Link to="/about" className="text-[#C4A36B] hover:text-[#042E0C] transition-colors">About ACRES →</Link>
              <Link to="/services" className="text-[#C4A36B] hover:text-[#042E0C] transition-colors">Our services →</Link>
              <Link to="/valuation" className="text-[#C4A36B] hover:text-[#042E0C] transition-colors">Free valuation →</Link>
              <Link to="/insights" className="text-[#C4A36B] hover:text-[#042E0C] transition-colors">All insights →</Link>
              <Link to="/contact" className="text-[#C4A36B] hover:text-[#042E0C] transition-colors">Contact ACRES →</Link>
            </div>
          </aside>
        </div>

        {/* Related topics */}
        <div className="max-w-5xl mx-auto mt-12 pt-12 border-t border-[#042E0C]/10">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#C4A36B] mb-4">Explore other topics</p>
          <div className="flex flex-wrap gap-2" data-testid="topic-related">
            {insightTopics.filter((t) => t.slug !== topic.slug).map((t) => (
              <Link
                key={t.slug}
                to={`/insights/topic/${t.slug}`}
                className="inline-flex items-center px-4 py-2 text-sm border border-[#042E0C]/15 hover:border-[#C4A36B] hover:bg-white text-[#042E0C] rounded-full transition-colors"
                data-testid={`topic-related-${t.slug}`}
              >
                {t.title}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InsightTopicPage;
